/* global BigInt */
// zpost-app/src/components/ensapp/PrimaryNameManager.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { JsonRpcProvider, namehash, getAddress } from 'ethers';
import { useAuth } from '../../contexts/AuthContext';
import useBridgeTransaction from '../../hooks/useBridgeTransaction';
import { getRPCProvider } from '../../utils/rpc'; // reuse RPC function

// CORRECT ENS addresses for mainnet
const NAME_WRAPPER_ADDRESS = '0x582224b8d4534F4749EFA4f22eF7241E0C56D4B8'; // Mainnet NameWrapper
const ENS_REVERSE_REGISTRAR = '0xa58E81fe9b61B5c3fE2AFD33CF304c454AbFc7Cb'; // CURRENT Mainnet Reverse Registrar

/**
 * PrimaryNameManager - using bridge transaction flow (mirrors ProfileRecordsManager)
 */
const PrimaryNameManager = ({ identityAuth, ensureSessionToken = null }) => {
  const { walletAddress, ensName, isAuthenticated } = identityAuth || {};

  // UI state
  const [isSetting, setIsSetting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [transactionStatus, setTransactionStatus] = useState('');

  // ENS domains and current primary
  const [allEnsNames, setAllEnsNames] = useState([]);
  const [currentPrimary, setCurrentPrimary] = useState('');

  // RPC provider
  const [stableProvider, setStableProvider] = useState(null);

  // Hooks
  const { wallet: authWallet, connectToZwallet } = useAuth();
  const { sendTransaction: bridgeSend } = useBridgeTransaction();

  // Receipt modal state
  const [receiptTxHash, setReceiptTxHash] = useState(null);

  // Ref to keep authWallet up‑to‑date in async callbacks
  const authWalletRef = useRef(authWallet);
  useEffect(() => {
    authWalletRef.current = authWallet;
    console.log('authWalletRef updated:', authWallet?.isConnected);
  }, [authWallet]);

  const ZPOST_ORIGIN = 'https://zpost.ai';

  // Initialize provider on mount
  useEffect(() => {
    let cancelled = false;
    const initProvider = async () => {
      try {
        const provider = await getRPCProvider();
        if (!cancelled) setStableProvider(provider);
      } catch (err) {
        console.error('Failed to initialize RPC provider:', err);
        setError('Could not connect to Ethereum network. Please refresh.');
      }
    };
    initProvider();
    return () => { cancelled = true; };
  }, []);

  // -------------------------
  // Comprehensive ENS domain discovery (same as original, uses stableProvider)
  // -------------------------
  const discoverCommonSubdomains = useCallback(async (walletAddr, baseDomains) => {
    if (!stableProvider) return [];
    const discovered = [];
    const commonSubdomains = ['www', 'app', 'api', 'blog', 'shop', 'www2', 'dashboard', 'admin'];

    for (const base of baseDomains) {
      const baseName = base.name;
      if (baseName.split('.').length === 2) {
        for (const sub of commonSubdomains) {
          const testDomain = `${sub}.${baseName}`;
          try {
            const resolver = await stableProvider.getResolver(testDomain);
            if (!resolver) continue;
            const address = await resolver.getAddress();
            if (address && address.toLowerCase() === walletAddr.toLowerCase()) {
              discovered.push({
                name: testDomain,
                labelName: sub,
                id: `subdomain-${testDomain}`,
                hasResolver: true,
                isZpostEth: testDomain.endsWith('.zpost.eth'),
                source: 'subdomain-discovery'
              });
            }
          } catch (e) {
            // ignore
          }
        }
      }
    }
    return discovered;
  }, [stableProvider]);

  // Enhanced GraphQL query to find domains
  const fetchDomainsWithEnhancedGraphQL = useCallback(async (walletAddr) => {
    try {
      const SUBGRAPH_URL = 'https://api.thegraph.com/subgraphs/name/ensdomains/ens';
      const query = `
        query GetDomains($owner: String!, $registrant: String!) {
          domainsAsOwner: domains(where: { owner: $owner }, first: 100) {
            id
            name
            labelName
            createdAt
            resolver { id address }
          }
          domainsAsRegistrant: registrations(where: { registrant: $registrant }, first: 100) {
            id
            domain { id name labelName createdAt resolver { id address } }
          }
          wrappedDomains: domains(where: { wrappedOwner: $owner }, first: 100) {
            id
            name
            labelName
            createdAt
            resolver { id address }
          }
        }
      `;

      const resp = await fetch(SUBGRAPH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          variables: { owner: walletAddr.toLowerCase(), registrant: walletAddr.toLowerCase() }
        })
      });

      const json = await resp.json();
      if (json.errors) {
        console.warn('GraphQL returned errors:', json.errors);
      }

      const results = [];

      if (json.data?.domainsAsOwner) {
        for (const d of json.data.domainsAsOwner) {
          if (!d.name || d.name.includes('.addr.reverse')) continue;
          results.push({
            name: d.name,
            labelName: d.labelName,
            id: d.id,
            createdAt: d.createdAt,
            hasResolver: !!(d.resolver && d.resolver.address),
            isZpostEth: d.name.endsWith('.zpost.eth'),
            source: 'graphql-owner'
          });
        }
      }

      if (json.data?.domainsAsRegistrant) {
        for (const r of json.data.domainsAsRegistrant) {
          const d = r.domain;
          if (!d || !d.name || d.name.includes('.addr.reverse')) continue;
          results.push({
            name: d.name,
            labelName: d.labelName,
            id: d.id,
            createdAt: d.createdAt,
            hasResolver: !!(d.resolver && d.resolver.address),
            isZpostEth: d.name.endsWith('.zpost.eth'),
            source: 'graphql-registrant'
          });
        }
      }

      if (json.data?.wrappedDomains) {
        for (const d of json.data.wrappedDomains) {
          if (!d.name || d.name.includes('.addr.reverse')) continue;
          results.push({
            name: d.name,
            labelName: d.labelName,
            id: d.id,
            createdAt: d.createdAt,
            hasResolver: !!(d.resolver && d.resolver.address),
            isZpostEth: d.name.endsWith('.zpost.eth'),
            source: 'graphql-wrapped'
          });
        }
      }

      return results;
    } catch (err) {
      console.warn('Enhanced GraphQL query failed:', err);
      return [];
    }
  }, []);

  // Comprehensive discovery combining reverse lookup, auth ENS, GraphQL and subdomains
  const fetchAllNamesFromENS = useCallback(async (walletAddr) => {
    if (!stableProvider) return [];
    const discovered = [];

    // 1) Reverse lookup
    try {
      const reverse = await stableProvider.lookupAddress(walletAddr);
      if (reverse) {
        try {
          const resolver = await stableProvider.getResolver(reverse);
          if (resolver) {
            const addr = await resolver.getAddress();
            if (addr && addr.toLowerCase() === walletAddr.toLowerCase()) {
              discovered.push({
                name: reverse,
                labelName: reverse.split('.')[0],
                id: `reverse-${reverse}`,
                hasResolver: true,
                isZpostEth: reverse.endsWith('.zpost.eth'),
                source: 'reverse-lookup'
              });
            }
          }
        } catch (e) { /* ignore */ }
      }
    } catch (e) { /* ignore */ }

    // 2) identityAuth ENS name
    if (ensName) {
      try {
        const resolver = await stableProvider.getResolver(ensName);
        if (resolver) {
          const addr = await resolver.getAddress();
          if (addr && addr.toLowerCase() === walletAddr.toLowerCase()) {
            if (!discovered.find(d => d.name === ensName)) {
              discovered.push({
                name: ensName,
                labelName: ensName.split('.')[0],
                id: `auth-${ensName}`,
                hasResolver: true,
                isZpostEth: ensName.endsWith('.zpost.eth'),
                source: 'identity-auth'
              });
            }
          }
        }
      } catch (e) { /* ignore */ }
    }

    // 3) GraphQL discovery
    try {
      const graphql = await fetchDomainsWithEnhancedGraphQL(walletAddr);
      for (const d of graphql) {
        if (!discovered.find(x => x.name === d.name)) discovered.push(d);
      }
    } catch (e) { /* ignore */ }

    // 4) Subdomain discovery for .zpost.eth domains
    try {
      const zposts = discovered.filter(d => d.isZpostEth);
      if (zposts.length > 0) {
        const subs = await discoverCommonSubdomains(walletAddr, zposts);
        for (const s of subs) {
          if (!discovered.find(x => x.name === s.name)) discovered.push(s);
        }
      }
    } catch (e) { /* ignore */ }

    // Dedupe & sort: zpost first then alphabetical
    const unique = discovered.reduce((acc, cur) => acc.find(x => x.name === cur.name) ? acc : [...acc, cur], []);
    unique.sort((a, b) => {
      const aZ = a.name.endsWith('.zpost.eth');
      const bZ = b.name.endsWith('.zpost.eth');
      if (aZ && !bZ) return -1;
      if (!aZ && bZ) return 1;
      return a.name.localeCompare(b.name);
    });

    return unique;
  }, [stableProvider, ensName, fetchDomainsWithEnhancedGraphQL, discoverCommonSubdomains]);

  // -------------------------
  // normalizeTx helper (kept from original)
  // -------------------------
  const normalizeTx = (tx) => {
    if (!tx || typeof tx !== 'object') return tx;
    const out = { ...tx };

    if (out.to && typeof out.to !== 'string') {
      try { out.to = String(out.to); } catch (e) { /* ignore */ }
    }

    try { if (out.to) out.to = getAddress(out.to); } catch (e) { /* ignore */ }

    if (out.data && typeof out.data === 'string' && !out.data.startsWith('0x')) {
      out.data = '0x' + out.data;
    }

    const v = out.value;
    if (v === undefined || v === null || v === '') {
      out.value = '0x0';
    } else if (typeof v === 'string') {
      const s = v.trim();
      if (s.startsWith('0x') || s.startsWith('0X')) {
        out.value = s;
      } else if (s.indexOf('.') >= 0) {
        const n = Number(s);
        if (!isNaN(n)) {
          try { out.value = '0x' + BigInt(Math.round(n * 1e18)).toString(16); } catch (e) { out.value = '0x0'; }
        } else {
          try { out.value = '0x' + BigInt(s).toString(16); } catch (e) { out.value = '0x0'; }
        }
      } else {
        try { out.value = '0x' + BigInt(s).toString(16); } catch (e) { out.value = '0x0'; }
      }
    } else if (typeof v === 'number') {
      try { out.value = '0x' + BigInt(Math.floor(v)).toString(16); } catch (e) { out.value = '0x0'; }
    } else if (typeof v === 'bigint') {
      out.value = '0x' + v.toString(16);
    } else {
      out.value = '0x0';
    }

    if (!out.chainId) out.chainId = '0x1';
    else if (typeof out.chainId === 'number') out.chainId = '0x' + Number(out.chainId).toString(16);
    else if (typeof out.chainId === 'string' && !out.chainId.startsWith('0x')) {
      try { out.chainId = '0x' + BigInt(out.chainId).toString(16); } catch (e) { out.chainId = '0x1'; }
    }

    if (!out.from && walletAddress) out.from = walletAddress;
    if (out.from) out.from = String(out.from);

    if (!out.gasLimit && !out.gas) out.gasLimit = '0x186a0';
    if (!out.gasPrice && !out.maxFeePerGas && !out.maxPriorityFeePerGas) {
      out.gasPrice = '0x2540be400';
    }

    return out;
  };

  // -------------------------
  // Fetch all names and current primary
  // -------------------------
  const fetchAllEnsNames = useCallback(async () => {
    if (!walletAddress) {
      setError('No wallet address available');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const primaryName = await stableProvider.lookupAddress(walletAddress);
      setCurrentPrimary(primaryName || '');

      const names = await fetchAllNamesFromENS(walletAddress);
      setAllEnsNames(names);
    } catch (err) {
      console.error('Failed to fetch ENS names:', err);
      setError('Failed to load your ENS names from the registry');
      setAllEnsNames([]);
    } finally {
      setIsLoading(false);
    }
  }, [walletAddress, stableProvider, fetchAllNamesFromENS]);

  // Initialize when provider and wallet are ready
  useEffect(() => {
    if (stableProvider && walletAddress) {
      fetchAllEnsNames();
    } else {
      setIsLoading(false);
    }
  }, [stableProvider, walletAddress, fetchAllEnsNames]);

  // -------------------------
  // sendSingleTransaction – simplified, uses bridge
  // -------------------------
  const sendSingleTransaction = async (domainName) => {
    if (!walletAddress) throw new Error('Wallet not connected');
    if (!domainName) throw new Error('No domain provided');
    if (!stableProvider) throw new Error('Network not ready');

    setIsSetting(true);
    setTransactionStatus('preparing');
    setError('');
    setSuccess('');

    // FIRST: Verify domain ownership before building transaction
    try {
      console.log(`🔍 Verifying ownership of ${domainName} for ${walletAddress}`);
      
      const resolvedAddress = await stableProvider.resolveName(domainName);
      if (!resolvedAddress || resolvedAddress.toLowerCase() !== walletAddress.toLowerCase()) {
        throw new Error(`Domain ${domainName} does not resolve to your wallet address. It resolves to: ${resolvedAddress || 'nothing'}`);
      }

      const resolver = await stableProvider.getResolver(domainName);
      if (!resolver) {
        throw new Error(`No resolver configured for ${domainName}`);
      }

      const resolverAddress = await resolver.getAddress();
      if (!resolverAddress || resolverAddress.toLowerCase() !== walletAddress.toLowerCase()) {
        throw new Error(`Resolver reports different address for ${domainName}`);
      }

      console.log(`✅ Domain ownership verified: ${domainName} -> ${walletAddress}`);
    } catch (verifyErr) {
      console.error('Domain ownership verification failed:', verifyErr);
      setIsSetting(false);
      setTransactionStatus('');
      setError(`Cannot set primary name: ${verifyErr.message}`);
      throw verifyErr;
    }

    const messageId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `msg-${Date.now()}-${Math.random().toString(36).slice(2,10)}`;

    // Build transaction payload (same logic as original)
    const buildTxPayload = async () => {
      // Check for wrapped domains using NameWrapper
      try {
        const code = await stableProvider.getCode(NAME_WRAPPER_ADDRESS);
        if (code && code !== '0x') {
          console.log(`🔧 Checking if ${domainName} is wrapped...`);

          const node = namehash(domainName);
          const ethersModule = await import('ethers');
          const nameWrapperIface = new ethersModule.Interface([
            "function ownerOf(uint256 id) public view returns (address)",
            "function setPrimaryName(bytes32 node, address owner) external"
          ]);

          // Try to get owner from NameWrapper to see if it's wrapped
          try {
            const ownerCallData = nameWrapperIface.encodeFunctionData('ownerOf', [node]);
            const ownerResult = await stableProvider.call({
              to: NAME_WRAPPER_ADDRESS,
              data: ownerCallData
            });

            if (ownerResult && ownerResult !== '0x') {
              // Domain is wrapped, use NameWrapper
              console.log(`🔧 Using NameWrapper for wrapped domain: ${domainName}`);
              const data = nameWrapperIface.encodeFunctionData('setPrimaryName', [node, walletAddress]);
              return {
                txPayload: {
                  to: NAME_WRAPPER_ADDRESS,
                  data,
                  value: '0x0'
                },
                display: null
              };
            }
          } catch (e) {
            console.log('Domain not wrapped or NameWrapper call failed, falling back to reverse registrar', e);
          }
        }
      } catch (e) {
        console.log('NameWrapper not available, falling back to reverse registrar', e);
      }

      // For non-wrapped domains, use the reverse registrar
      try {
        const ethersModule = await import('ethers');
        const iface = new ethersModule.Interface(["function setName(string name)"]);
        const data = iface.encodeFunctionData('setName', [String(domainName)]);
        console.log(`🔧 Using CURRENT Reverse Registrar for domain: ${domainName}`);
        return { 
          txPayload: { 
            to: ENS_REVERSE_REGISTRAR, 
            data, 
            value: '0x0' 
          }, 
          display: null 
        };
      } catch (err) {
        console.error('Failed to build transaction:', err);
        throw new Error(`Failed to prepare primary name transaction: ${err.message}`);
      }
    };

    let txPayload, display;
    try {
      ({ txPayload, display } = await buildTxPayload());
    } catch (buildErr) {
      console.error('[sendSingleTransaction] Failed to build/prepare tx:', buildErr);
      setIsSetting(false);
      setTransactionStatus('');
      setError(buildErr?.message || String(buildErr));
      throw buildErr;
    }

    // Normalize transaction
    const normalizedTxPayload = (() => {
      try {
        const n = normalizeTx(txPayload || {});
        if (!n.chainId) n.chainId = '0x1';
        if (!n.from && walletAddress) n.from = walletAddress;
        if (n.to) try { n.to = String(n.to); } catch (e) {}
        return n;
      } catch (e) {
        console.debug('[sendSingleTransaction] normalization fallback failed', e);
        return txPayload || {};
      }
    })();

    // Pre‑flight gas check (optional)
    let preflightGasEstimate = null;
    try {
      preflightGasEstimate = await stableProvider.estimateGas({
        to: normalizedTxPayload.to,
        data: normalizedTxPayload.data,
        value: normalizedTxPayload.value || '0x0',
        from: normalizedTxPayload.from || walletAddress
      }).catch((estimateError) => {
        console.warn('Gas estimation failed, using safe default:', estimateError);
        return BigInt(100000);
      });
    } catch (preErr) {
      console.warn('[preflight] gas estimation failed, using safe default:', preErr);
      preflightGasEstimate = BigInt(100000);
    }

    // Final transaction object for the bridge
    const tx = {
      to: normalizedTxPayload.to,
      data: normalizedTxPayload.data,
      value: normalizedTxPayload.value || '0x0',
      from: normalizedTxPayload.from || walletAddress,
      ...(preflightGasEstimate ? { gasLimit: preflightGasEstimate } : {})
    };

    // Send via bridge
    try {
      console.log('🔑 Using bridge transaction');
      const result = await bridgeSend(tx);
      console.log('✅ Transaction confirmed:', result);

      // Show receipt modal with block number and Etherscan link
      setReceiptTxHash({
        hash: result.hash,
        blockNumber: result.blockNumber
      });

      setIsSetting(false);
      setTransactionStatus('confirmed');
      return { success: true, hash: result.hash };
    } catch (err) {
      console.error('Transaction failed:', err);
      setIsSetting(false);
      setTransactionStatus('failed');
      setError(err.message || 'Transaction failed');
      throw err;
    }
  };

  // setAsPrimary wrapper with error handling and wallet unlock wait
  const setAsPrimary = async (domainName) => {
    if (!walletAddress) {
      setError('Wallet not connected');
      return;
    }

    // If wallet not connected, open unlock modal and wait
    if (!authWalletRef.current?.isConnected) {
      console.log('Wallet not connected, opening wallet modal via connectToZwallet');
      connectToZwallet();

      let walletConnected = false;
      console.log('⏳ Waiting for wallet to connect (max 300 seconds)...');
      for (let attempt = 0; attempt < 300; attempt++) {
        if (authWalletRef.current?.isConnected) {
          walletConnected = true;
          console.log(`✅ Wallet connected after ${attempt} seconds`);
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      if (!walletConnected) {
        setError('Wallet not ready. Please unlock your wallet and try again.');
        return;
      }
    }

    try {
      const result = await sendSingleTransaction(domainName);
      
      if (result && result.success) {
        setCurrentPrimary(domainName);
        // Refresh the list after a delay
        setTimeout(() => {
          fetchAllEnsNames().catch(() => {});
        }, 5000);
      }
    } catch (err) {
      console.error('setAsPrimary error:', err);
      // Specific error messages (same as original)
      if (err.message.includes('decrypt') || err.message.includes('Malformed UTF-8')) {
        setError('Wallet decryption failed. Please unlock your wallet or reset it in the wallet app.');
      } else if (err.message.includes('gas') || err.message.includes('ETH')) {
        setError(err.message);
      } else if (err.message.includes('locked') || err.message.includes('not ready')) {
        setError(err.message);
      } else if (err.message.includes('popup')) {
        setError('Please allow popups for zpost.ai to confirm transactions.');
      } else {
        setError(`Failed to set primary name: ${err.message}`);
      }
    }
  };

  const refreshNames = () => {
    fetchAllEnsNames();
    setSuccess('ENS names refreshed!');
    setTimeout(() => setSuccess(''), 3000);
  };

  // UI lists
  const zpostDomains = allEnsNames.filter(d => d.isZpostEth);
  const otherDomains = allEnsNames.filter(d => !d.isZpostEth);

  if (isLoading || !stableProvider) {
    return (
      <div className="primary-name-container">
        <h2>Manage Primary ENS Name</h2>
        <div className="identity-card" style={{ textAlign: 'center', padding: '3rem' }}>
          <p style={{ color: 'var(--gold)' }}>Loading your ENS names...</p>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '1rem' }}>
            Connecting to Ethereum network...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="primary-name-container">
      <h2>Manage Primary ENS Name</h2>

      <div className="primary-name-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <p className="text-secondary" style={{ margin: 0 }}>
              Choose which ENS name to display as your primary identity across Web3.
            </p>
            {currentPrimary && (
              <p style={{ color: 'var(--gold)', fontSize: '0.9rem', margin: '0.5rem 0 0 0' }}>
                Current Primary: <strong>{currentPrimary}</strong>
              </p>
            )}
          </div>
          <button
            onClick={refreshNames}
            className="btn btn-gold"
            style={{ fontSize: '0.8rem', padding: '0.5rem 1rem' }}
          >
            Refresh
          </button>
        </div>

        {transactionStatus && (
          <div className={`status-message`} style={{ 
            background: 'var(--black)', 
            border: '1px solid var(--gold)',
            padding: '12px', 
            borderRadius: '8px', 
            marginBottom: '12px' 
          }}>
            <p style={{ 
              margin: 0, 
              fontWeight: 'bold', 
              color: transactionStatus === 'failed' ? '#ff4444' : 'var(--gold)'
            }}>
              {transactionStatus === 'preparing' && 'Preparing transaction...'}
              {transactionStatus === 'confirmed' && 'Primary name updated!'}
              {transactionStatus === 'failed' && 'Primary name update failed'}
            </p>
          </div>
        )}

        {success && <div className="status-message status-success">{success}</div>}
        {error && <div className="status-message status-error">{error}</div>}

        {zpostDomains.length > 0 && (
          <div className="domain-section" style={{ marginBottom: '2rem' }}>
            <h3 style={{ color: 'var(--gold)', marginBottom: '1rem' }}>
              Your .zpost.eth Domains ({zpostDomains.length})
            </h3>
            <ul className="domain-list" style={{ listStyle: 'none', padding: 0 }}>
              {zpostDomains.map(domain => (
                <DomainItem
                  key={domain.id}
                  domain={domain}
                  isCurrentPrimary={domain.name === currentPrimary}
                  isSetting={isSetting}
                  onSetPrimary={setAsPrimary}
                  walletReady={authWalletRef.current?.isConnected}
                />
              ))}
            </ul>
          </div>
        )}

        {otherDomains.length > 0 && (
          <div className="domain-section">
            <h3 style={{ color: 'var(--text-light)', marginBottom: '1rem' }}>
              Your Other ENS Domains ({otherDomains.length})
            </h3>
            <ul className="domain-list" style={{ listStyle: 'none', padding: 0 }}>
              {otherDomains.map(domain => (
                <DomainItem
                  key={domain.id}
                  domain={domain}
                  isCurrentPrimary={domain.name === currentPrimary}
                  isSetting={isSetting}
                  onSetPrimary={setAsPrimary}
                  walletReady={authWalletRef.current?.isConnected}
                />
              ))}
            </ul>
          </div>
        )}

        {allEnsNames.length === 0 && walletAddress && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
              No ENS names found for your wallet address.
            </p>
            <a href="/identity" className="btn btn-gold" style={{ textDecoration: 'none' }}>
              Mint .zpost.eth Domain
            </a>
          </div>
        )}

        {!walletAddress && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: 'var(--text-secondary)' }}>
              Please connect your wallet to manage primary names.
            </p>
          </div>
        )}

        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          background: 'rgba(255, 215, 0, 0.1)',
          borderRadius: '8px',
          border: '1px solid var(--gold)'
        }}>
          <h4 style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>About Primary Names</h4>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
            Your primary ENS name is displayed when people look up your address. Dapps use this as your main Web3 identity.
            You can only have one primary name at a time across all your ENS domains.
          </p>
        </div>
      </div>

      {/* Receipt Modal */}
      {receiptTxHash && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.95)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--card-bg)',
            padding: '30px',
            borderRadius: '15px',
            border: '2px solid var(--gold)',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 0 50px rgba(255,215,0,0.3)'
          }}>
            <h3 style={{ color: 'var(--gold)', margin: '0 0 20px 0', fontSize: '24px', textAlign: 'center' }}>
              Transaction Confirmed
            </h3>
            <div style={{
              background: 'var(--black)',
              padding: '20px',
              borderRadius: '10px',
              border: '1px solid var(--gold)',
              marginBottom: '20px'
            }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '15px' }}>
                Your transaction has been successfully mined.
              </p>
              {receiptTxHash.blockNumber && (
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ color: 'var(--gold)' }}>Block:</strong>
                  <span style={{ color: 'var(--text-light)', marginLeft: '5px' }}>
                    {receiptTxHash.blockNumber}
                  </span>
                </div>
              )}
              <div style={{
                background: 'rgba(255,215,0,0.1)',
                padding: '10px',
                borderRadius: '5px',
                wordBreak: 'break-all',
                marginBottom: '15px'
              }}>
                <a
                  href={`https://etherscan.io/tx/${receiptTxHash.hash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--gold)', textDecoration: 'underline' }}
                >
                  {receiptTxHash.hash}
                </a>
              </div>
              <a
                href={`https://etherscan.io/tx/${receiptTxHash.hash}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'block',
                  textAlign: 'center',
                  background: 'var(--gold)',
                  color: 'var(--black)',
                  padding: '10px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  marginBottom: '10px'
                }}
              >
                View on Etherscan
              </a>
            </div>
            <button
              onClick={() => setReceiptTxHash(null)}
              style={{
                width: '100%',
                background: 'transparent',
                color: '#ff4444',
                border: '1px solid #ff4444',
                padding: '15px',
                borderRadius: '8px',
                fontWeight: 'bold',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// DomainItem component (unchanged)
const DomainItem = ({ domain, isCurrentPrimary, isSetting, onSetPrimary, walletReady }) => {
  return (
    <li style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 15px',
      border: '1px solid var(--border)',
      borderRadius: '5px',
      margin: '8px 0',
      background: isCurrentPrimary ? 'rgba(255, 215, 0, 0.1)' : 'var(--black)',
      borderLeft: isCurrentPrimary ? '4px solid var(--gold)' : '1px solid var(--border)',
      transition: 'all 0.3s ease'
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span className="domain-name" style={{
            color: 'var(--text-light)',
            fontWeight: isCurrentPrimary ? 'bold' : 'normal',
            fontFamily: 'monospace',
            fontSize: '1.1rem'
          }}>
            {domain.name}
          </span>
          {domain.isZpostEth && (
            <span style={{
              background: 'var(--gold)',
              color: 'var(--black)',
              padding: '2px 6px',
              borderRadius: '3px',
              fontSize: '0.7rem',
              fontWeight: 'bold'
            }}>
              .zpost.eth
            </span>
          )}
        </div>
        {!domain.hasResolver && (
          <div style={{ color: '#ffa500', fontSize: '0.8rem', marginTop: '0.25rem' }}>
            Resolver not configured
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        {isCurrentPrimary && (
          <span style={{
            background: 'var(--gold)',
            color: 'var(--black)',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            Primary
          </span>
        )}
        <button
          onClick={() => onSetPrimary(domain.name)}
          disabled={isCurrentPrimary || isSetting || !walletReady}
          className="set-primary-btn"
          style={{
            opacity: (isCurrentPrimary || isSetting || !walletReady) ? 0.6 : 1,
            cursor: (isCurrentPrimary || isSetting || !walletReady) ? 'not-allowed' : 'pointer',
            minWidth: '120px'
          }}
        >
          {isSetting ? 'Setting...' : 'Set Primary'}
        </button>
      </div>
    </li>
  );
};

export default PrimaryNameManager;
